<template>
  <controller>
    <main class="responses-page">
      <header class="responses-header">
        <div>
          <router-link to="/appscripts/sgu">&larr; Voltar ao dashboard</router-link>
          <h1>Respostas do SGU</h1>
          <p>Visão consolidada com autoria original e histórico de edição.</p>
        </div>
        <div class="responses-header-actions">
          <label>
            <span>Ciclo</span>
            <select v-model="cycleId" :disabled="loading || saving" @change="loadResponses">
              <option v-for="cycle in cycles" :key="cycle.id" :value="cycle.id">{{ cycle.code }}</option>
            </select>
          </label>
          <button
            type="button"
            class="header-report-button"
            :disabled="!selectedReportUnit"
            :title="selectedReportUnit ? `Abrir relatório da unidade ${selectedReportUnit.code}` : 'Selecione uma unidade no filtro'"
            @click="openUnitReport"
          >
            Relatórios
          </button>
        </div>
      </header>

      <form-spinner v-if="loading" />

      <section v-else-if="errorMessage" class="state-message error" role="alert">
        <p>{{ errorMessage }}</p>
        <button type="button" @click="loadResponses">Tentar novamente</button>
      </section>

      <template v-else>
        <section class="response-progress-card" aria-label="Acompanhamento das unidades">
          <div class="response-progress-heading">
            <div>
              <strong>Unidades que responderam</strong>
              <small>Autoavaliação DU</small>
            </div>
            <span>{{ completionLabel }}</span>
          </div>
          <div class="unit-response-summary">
            <article class="answered-units">
              <strong>{{ respondedUnits.length }}</strong>
              <span>de {{ units.length }} unidades responderam</span>
            </article>
            <article :class="{ complete: !pendingUnits.length }">
              <strong>{{ pendingUnits.length }}</strong>
              <span>{{ pendingUnits.length === 1 ? 'unidade pendente' : 'unidades pendentes' }}</span>
            </article>
          </div>
          <div v-if="pendingUnits.length" class="pending-units">
            <strong>Unidades pendentes</strong>
            <ul>
              <li v-for="unit in pendingUnits" :key="unit.id">{{ unit.code }}<span v-if="unit.name"> · {{ unit.name }}</span></li>
            </ul>
          </div>
          <p v-else class="all-answered">Todas as unidades concluíram a autoavaliação.</p>
        </section>

        <section class="filters-card">
          <label><span>Área</span><select v-model="filters.areaId"><option value="">Todas</option><option v-for="area in areas" :key="area.id" :value="String(area.id)">{{ area.name }}</option></select></label>
          <label><span>Unidade</span><select v-model="filters.unitId"><option value="">Todas</option><option v-for="unit in units" :key="unit.id" :value="String(unit.id)">{{ unit.code }}</option></select></label>
          <label><span>Camada</span><select v-model="filters.layer"><option value="">Todas</option><option v-for="layer in layers" :key="layer.value" :value="layer.value">{{ layer.label }}</option></select></label>
          <label><span>Origem</span><select v-model="filters.assessmentType"><option value="">Todas</option><option value="csc">Avaliação CSC</option><option value="self_assessment">Autoavaliação DU</option></select></label>
          <label class="search"><span>Buscar</span><input v-model.trim="filters.search" type="search" placeholder="Requisito, área ou responsável"></label>
          <div class="report-actions" aria-live="polite">
            <div v-if="selectedReportUnit" class="publication-status">
              <span :class="['publication-badge', publicationBadgeClass]">{{ publicationStatusLabel }}</span>
              <small>{{ publicationStatusDescription }}</small>
            </div>
            <div v-else class="publication-status">
              <strong>Publicação do relatório</strong>
              <small>Selecione uma unidade para consultar ou alterar a publicação.</small>
            </div>
            <div v-if="selectedReportUnit" class="publication-buttons">
              <button type="button" class="secondary report-button" :disabled="publishing" @click="openUnitReport">
                Visualizar relatório
              </button>
              <button
                v-if="selectedPublication?.published"
                type="button"
                class="secondary report-button"
                :disabled="publishing"
                @click="copyPublicationLink"
              >
                Copiar link
              </button>
              <button
                v-if="canPublishReports && !selectedPublication?.published"
                type="button"
                class="primary report-button"
                :disabled="publishing || !selectedPublication?.ready"
                @click="publishSelectedReport"
              >
                {{ publishing ? 'Publicando...' : 'Publicar relatório' }}
              </button>
              <button
                v-if="canPublishReports && selectedPublication?.published"
                type="button"
                class="danger report-button"
                :disabled="publishing"
                @click="revokeSelectedReport"
              >
                {{ publishing ? 'Revogando...' : 'Despublicar' }}
              </button>
            </div>
          </div>
          <p v-if="publicationError" class="report-error" role="alert">{{ publicationError }}</p>
        </section>

        <section class="table-card">
          <div class="table-heading"><strong>Respostas</strong><span>{{ filteredResponses.length }} registros</span></div>
          <div v-if="filteredResponses.length" class="table-wrap">
            <table>
              <thead><tr><th>Unidade</th><th>Área / requisito</th><th>Origem</th><th>Peso</th><th>Camada</th><th>Anexos</th><th>Respondida por</th><th>Última edição</th><th></th></tr></thead>
              <tbody>
                <tr v-for="response in filteredResponses" :key="response.id">
                  <td><strong>{{ response.unit.code }}</strong></td>
                  <td><small>{{ response.area.name }}</small><strong>{{ response.requirement.name }}</strong></td>
                  <td>{{ response.assessmentTypeLabel }}</td>
                  <td>{{ formatWeight(response.requirement.weight) }}</td>
                  <td><span :class="['layer-badge', layerClass(response.layer)]">{{ response.layerLabel }}</span></td>
                  <td>
                    <button v-for="attachment in response.attachments" :key="attachment.id" type="button" class="attachment-button" @click="downloadAttachment(attachment)">{{ attachment.name }}</button>
                    <span v-if="!response.attachments.length" class="never-edited">Nenhum</span>
                  </td>
                  <td><strong>{{ userLabel(response.answeredBy) }}</strong><small>{{ formatDate(response.answeredAt) }}</small></td>
                  <td><template v-if="response.lastEditedAt"><strong>{{ userLabel(response.lastEditedBy) }}</strong><small>{{ formatDate(response.lastEditedAt) }} · {{ response.editCount }} {{ response.editCount === 1 ? 'edição' : 'edições' }}</small></template><span v-else class="never-edited">Nunca editada</span></td>
                  <td><button type="button" class="edit-button" @click="openEditor(response)">{{ canEditResponses ? 'Visualizar e editar' : 'Visualizar' }}</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty">Nenhuma resposta encontrada com os filtros selecionados.</p>
        </section>

        <section class="table-card divergence-card">
          <div class="table-heading">
            <div><strong>Divergências CSC × Diretor de Unidade</strong><small>Somente diferenças de dois ou mais níveis</small></div>
            <span>{{ divergenceRows.length }} divergências</span>
          </div>
          <div v-if="divergenceRows.length" class="table-wrap">
            <table class="divergence-table">
              <thead><tr><th>Unidade</th><th>Área / requisito</th><th>CSC</th><th>Diretor de Unidade</th><th>Diferença</th></tr></thead>
              <tbody>
                <tr v-for="row in divergenceRows" :key="row.key">
                  <td><strong>{{ row.csc.unit.code }}</strong></td>
                  <td><small>{{ row.csc.area.name }}</small><strong>{{ row.csc.requirement.name }}</strong></td>
                  <td><span :class="['layer-badge', layerClass(row.csc.layer)]">{{ row.csc.layerLabel }}</span></td>
                  <td><span :class="['layer-badge', layerClass(row.director.layer)]">{{ row.director.layerLabel }}</span></td>
                  <td>{{ row.difference }} níveis</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty">Não há divergências de dois ou mais níveis nos filtros selecionados.</p>
        </section>
      </template>

      <div v-if="editingResponse" class="sgu-editor-overlay" role="presentation" @click.self="closeEditor">
        <section class="editor-modal" role="dialog" aria-modal="true" aria-labelledby="editor-title">
          <header><div><small>{{ editingResponse.unit.code }} · {{ editingResponse.area.name }}</small><h2 id="editor-title">{{ editingResponse.requirement.name }}</h2></div><button type="button" aria-label="Fechar" @click="closeEditor">&times;</button></header>
          <div class="audit-line"><span>Resposta original: <strong>{{ userLabel(editingResponse.answeredBy) }}</strong> em {{ formatDate(editingResponse.answeredAt) }}</span><span v-if="editingResponse.lastEditedAt">Última edição: <strong>{{ userLabel(editingResponse.lastEditedBy) }}</strong> em {{ formatDate(editingResponse.lastEditedAt) }}</span></div>
          <div class="reference-grid"><article><strong>Básica</strong><p>{{ editingResponse.requirement.basicDescription }}</p></article><article><strong>Sustentável</strong><p>{{ editingResponse.requirement.sustainableDescription }}</p></article><article><strong>Alta Performance</strong><p>{{ editingResponse.requirement.highPerformanceDescription }}</p></article></div>
          <div class="editor-fields">
            <label><span>Camada *</span><select v-model="editForm.layer" :disabled="!canEditResponses"><option v-for="layer in layers" :key="layer.value" :value="layer.value">{{ layer.label }}</option></select></label>
            <label><span>Justificativa <b v-if="requiresJustification">*</b></span><textarea v-model.trim="editForm.justification" rows="5" maxlength="5000" :disabled="!canEditResponses" /></label>
            <label><span>Link da evidência</span><input v-model.trim="editForm.evidenceLink" type="url" maxlength="2048" :disabled="!canEditResponses"></label>
            <a v-if="editingResponse.evidenceLink" :href="editingResponse.evidenceLink" target="_blank" rel="noopener noreferrer">Abrir evidência atual</a>
          </div>
          <p v-if="editorError" class="editor-error" role="alert">{{ editorError }}</p>
          <footer><button type="button" class="secondary" :disabled="saving" @click="closeEditor">{{ canEditResponses ? 'Cancelar' : 'Fechar' }}</button><button v-if="canEditResponses" type="button" class="primary" :disabled="saving" @click="saveResponse">{{ saving ? 'Salvando...' : 'Salvar alteração' }}</button></footer>
        </section>
      </div>
    </main>
  </controller>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import FormSpinner from '@/components/utilities/FormSpinner.vue'
import ImportedModulesService from '../services/importedModules.service'

const router = useRouter()
const cycles = ref([])
const areas = ref([])
const units = ref([])
const layers = ref([])
const responses = ref([])
const cycleId = ref('')
const loading = ref(true)
const saving = ref(false)
const publishing = ref(false)
const errorMessage = ref('')
const editorError = ref('')
const publicationError = ref('')
const editingResponse = ref(null)
const canEditResponses = ref(false)
const canPublishReports = ref(false)
const filters = reactive({ areaId: '', unitId: '', layer: '', assessmentType: 'csc', search: '' })
const editForm = reactive({ layer: '', justification: '', evidenceLink: '' })
const selectedReportUnit = computed(() => units.value.find(unit => String(unit.id) === filters.unitId) || null)
const selectedPublication = computed(() => selectedReportUnit.value?.reportPublication || null)
const publicationBadgeClass = computed(() => {
  if (selectedPublication.value?.published) return 'published'
  return selectedPublication.value?.ready ? 'ready' : 'pending'
})
const publicationStatusLabel = computed(() => {
  if (selectedPublication.value?.published) return 'Publicado'
  return selectedPublication.value?.ready ? 'Pronto para publicar' : 'Aguardando avaliação CSC'
})
const publicationStatusDescription = computed(() => {
  const publication = selectedPublication.value
  if (!publication) return 'O status de publicação não está disponível.'
  if (publication.published) {
    const status = publication.evaluated < publication.total ? 'Relatório parcial publicado' : 'Publicado'
    return `${status} em ${formatDate(publication.publishedAt)}. O link está ativo.`
  }
  if (publication.ready && publication.evaluated === publication.total) return 'Todas as respostas do CSC foram concluídas para esta unidade.'
  if (publication.ready) return `${publication.evaluated} de ${publication.total} requisitos do CSC concluídos. O relatório parcial pode ser publicado.`
  return 'Conclua ao menos um requisito do CSC para publicar o relatório.'
})

const filteredResponses = computed(() => {
  const term = filters.search.toLocaleLowerCase('pt-BR')
  return responses.value.filter(response => {
    if (filters.areaId && String(response.area.id) !== filters.areaId) return false
    if (filters.unitId && String(response.unit.id) !== filters.unitId) return false
    if (filters.layer && response.layer !== filters.layer) return false
    if (filters.assessmentType && response.assessmentType !== filters.assessmentType) return false
    if (!term) return true
    return [response.unit.code, response.area.name, response.requirement.name, response.answeredBy?.email, response.lastEditedBy?.email]
      .filter(Boolean)
      .some(value => String(value).toLocaleLowerCase('pt-BR').includes(term))
  })
})
const respondedUnits = computed(() => {
  const ids = new Set(responses.value
    .filter(response => response.assessmentType === 'self_assessment')
    .map(response => String(response.unit.id)))

  return units.value.filter(unit => ids.has(String(unit.id)))
})
const pendingUnits = computed(() => {
  const respondedIds = new Set(respondedUnits.value.map(unit => String(unit.id)))
  return units.value.filter(unit => !respondedIds.has(String(unit.id)))
})
const divergenceRows = computed(() => {
  const layerPoints = { not_met: 0, basic: 1, sustainable: 2, high_performance: 3 }
  const pairs = new Map()

  responses.value.filter(response => {
    if (filters.areaId && String(response.area.id) !== filters.areaId) return false
    if (filters.unitId && String(response.unit.id) !== filters.unitId) return false
    if (!filters.search) return true
    const term = filters.search.toLocaleLowerCase('pt-BR')
    return [response.unit.code, response.area.name, response.requirement.name]
      .some(value => String(value).toLocaleLowerCase('pt-BR').includes(term))
  }).forEach(response => {
    const key = `${response.unit.id}:${response.requirement.id}`
    const pair = pairs.get(key) || { key }
    pair[response.assessmentType === 'self_assessment' ? 'director' : 'csc'] = response
    pairs.set(key, pair)
  })

  return Array.from(pairs.values())
    .filter(pair => pair.csc && pair.director && pair.csc.layer !== pair.director.layer)
    .filter(pair => !filters.layer || pair.csc.layer === filters.layer || pair.director.layer === filters.layer)
    .map(pair => ({
      ...pair,
      difference: Number.isInteger(layerPoints[pair.csc.layer]) && Number.isInteger(layerPoints[pair.director.layer])
        ? Math.abs(layerPoints[pair.csc.layer] - layerPoints[pair.director.layer])
        : null
    }))
    .filter(pair => pair.difference >= 2)
    .sort((left, right) => right.difference - left.difference)
})
const requiresJustification = computed(() => ['not_met', 'basic', 'sustainable'].includes(editForm.layer))
const completionLabel = computed(() => pendingUnits.value.length
  ? `${pendingUnits.value.length} ${pendingUnits.value.length === 1 ? 'pendente' : 'pendentes'}`
  : 'Ciclo completo')

onMounted(loadResponses)

async function loadResponses () {
  loading.value = true
  errorMessage.value = ''
  try {
    const { data } = await ImportedModulesService.getSguResponses(cycleId.value || undefined)
    cycles.value = data.cycles || []
    areas.value = data.areas || []
    units.value = data.units || []
    layers.value = data.layers || []
    responses.value = data.responses || []
    canEditResponses.value = Boolean(data.canEdit)
    canPublishReports.value = Boolean(data.canPublish)
    cycleId.value = data.selectedCycleId
  } catch (error) {
    if (error.response?.status === 403) {
      router.replace({ name: 'Sem Permissão' })
      return
    }
    errorMessage.value = error.response?.data?.message || 'Não foi possível carregar as respostas do SGU.'
  } finally {
    loading.value = false
  }
}

function openEditor (response) {
  editingResponse.value = response
  editForm.layer = response.layer
  editForm.justification = response.justification || ''
  editForm.evidenceLink = response.evidenceLink || ''
  editorError.value = ''
}

function closeEditor () {
  if (saving.value) return
  editingResponse.value = null
  editorError.value = ''
}

async function saveResponse () {
  if (!canEditResponses.value) return
  if (!validateEdit()) return
  saving.value = true
  editorError.value = ''
  try {
    const { data } = await ImportedModulesService.updateSguResponse(editingResponse.value.id, {
      layer: editForm.layer,
      justification: editForm.layer === 'not_applicable' ? null : (editForm.justification || null),
      evidenceLink: editForm.layer === 'not_applicable' ? null : (editForm.evidenceLink || null)
    })
    const index = responses.value.findIndex(response => response.id === data.response.id)
    if (index >= 0) responses.value.splice(index, 1, data.response)
    editingResponse.value = null
    await loadResponses()
    window.toaster?.emit('open', { style: 'success', message: data.message, floater: true })
  } catch (error) {
    if (error.response?.status === 403) {
      router.replace({ name: 'Sem Permissão' })
      return
    }
    editorError.value = firstValidationError(error) || error.response?.data?.message || 'Não foi possível salvar a alteração.'
  } finally {
    saving.value = false
  }
}

function validateEdit () {
  if (!editForm.layer) return setEditorError('Selecione uma camada.')
  if (requiresJustification.value && !editForm.justification) return setEditorError('A justificativa é obrigatória para esta camada.')
  if (editForm.evidenceLink && !isValidHttpUrl(editForm.evidenceLink)) return setEditorError('O link de evidência informado não é válido.')
  editorError.value = ''
  return true
}

function setEditorError (message) { editorError.value = message; return false }
function isValidHttpUrl (value) { try { return ['http:', 'https:'].includes(new URL(value).protocol) } catch (_) { return false } }
function firstValidationError (error) { const errors = error.response?.data?.errors; return errors ? Object.values(errors).flat()[0] : '' }
function layerClass (layer) {
  if (layer === 'high_performance') return 'high'
  if (layer === 'not_applicable') return 'not-applicable'
  return layer === 'not_met' ? 'not-met' : layer
}
function userLabel (user) { return user?.name || user?.email || 'Usuário não localizado' }
function formatDate (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '-' }
function formatWeight (value) { return Number(value || 1).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) }
function openUnitReport () {
  if (!selectedReportUnit.value) return

  router.push({
    name: 'SGU - Relatório da Unidade',
    params: { unitId: selectedReportUnit.value.id },
    query: { cycleId: cycleId.value }
  })
}

async function publishSelectedReport () {
  if (!selectedReportUnit.value || !selectedPublication.value?.ready || publishing.value) return
  const isPartial = selectedPublication.value.evaluated < selectedPublication.value.total
  const confirmationMessage = isPartial
    ? `Publicar o relatório parcial da unidade ${selectedReportUnit.value.code}? O Diretor terá acesso às respostas disponíveis até o momento.`
    : `Publicar o relatório da unidade ${selectedReportUnit.value.code}? O Diretor terá acesso ao conteúdo.`
  if (!window.confirm(confirmationMessage)) return

  publishing.value = true
  publicationError.value = ''
  try {
    const { data } = await ImportedModulesService.publishSguReport(cycleId.value, selectedReportUnit.value.id)
    updateSelectedPublication(data.publication)
    window.toaster?.emit('open', { style: 'success', message: data.message, floater: true })
  } catch (error) {
    publicationError.value = firstValidationError(error) || error.response?.data?.message || 'Não foi possível publicar o relatório.'
  } finally {
    publishing.value = false
  }
}

async function revokeSelectedReport () {
  if (!selectedReportUnit.value || !selectedPublication.value?.published || publishing.value) return
  if (!window.confirm(`Despublicar o relatório da unidade ${selectedReportUnit.value.code}? O link atual deixará de funcionar.`)) return

  publishing.value = true
  publicationError.value = ''
  try {
    const { data } = await ImportedModulesService.revokeSguReport(cycleId.value, selectedReportUnit.value.id)
    updateSelectedPublication(data.publication)
    window.toaster?.emit('open', { style: 'success', message: data.message, floater: true })
  } catch (error) {
    publicationError.value = firstValidationError(error) || error.response?.data?.message || 'Não foi possível despublicar o relatório.'
  } finally {
    publishing.value = false
  }
}

async function copyPublicationLink () {
  if (!selectedPublication.value?.uuid) return

  const href = router.resolve({
    name: 'SGU - Relatório da Unidade',
    params: { unitId: selectedPublication.value.uuid }
  }).href
  const link = new URL(href, window.location.origin).toString()

  try {
    await navigator.clipboard.writeText(link)
    window.toaster?.emit('open', { style: 'success', message: 'Link do relatório copiado.', floater: true })
  } catch (_) {
    publicationError.value = 'Não foi possível copiar o link automaticamente.'
  }
}

function updateSelectedPublication (publication) {
  const index = units.value.findIndex(unit => unit.id === selectedReportUnit.value?.id)
  if (index < 0) return

  units.value.splice(index, 1, {
    ...units.value[index],
    reportPublication: publication
  })
}
async function downloadAttachment (attachment) {
  try {
    const response = await ImportedModulesService.downloadSguAttachment(attachment.id)
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.name
    link.click()
    URL.revokeObjectURL(url)
  } catch (_) {
    errorMessage.value = 'Não foi possível baixar o anexo.'
  }
}
</script>

<style scoped>
.responses-header-actions{align-items:center;display:flex;gap:10px}.header-report-button{background:#fff;border:1px solid #fff;border-radius:7px;box-sizing:border-box;color:#a5001a;cursor:pointer;font:inherit;font-size:12px;font-weight:700;height:44px;min-height:44px;padding:0 16px}.header-report-button:disabled{background:rgba(255,255,255,.55);border-color:transparent;color:#7d555b;cursor:not-allowed}.header-report-button:focus-visible{outline:3px solid #fff;outline-offset:3px}
.responses-page{background:#f5f6f8;min-height:100vh;padding:24px}.responses-header{align-items:flex-end;background:linear-gradient(135deg,#8f0017,#c0001d);border-radius:12px;color:#fff;display:flex;justify-content:space-between;margin-bottom:18px;padding:24px}.responses-header a{color:#ffd9df;font-size:12px}.responses-header h1{font-size:24px;margin:8px 0 4px}.responses-header p{margin:0;opacity:.9}.responses-header label,.filters-card label,.editor-fields label{display:grid;gap:5px}.responses-header label span,.filters-card label span,.editor-fields label span{font-size:11px;font-weight:700;text-transform:uppercase}.responses-header select,.filters-card select,.filters-card input,.editor-fields select,.editor-fields textarea,.editor-fields input{background:#fff;border:1px solid #d8dbe2;border-radius:7px;min-height:38px;padding:8px 10px}.response-progress-card{background:#fff;border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;padding:16px}.response-progress-heading{align-items:center;display:flex;justify-content:space-between}.response-progress-heading div{display:grid;gap:3px}.response-progress-heading small,.response-progress-heading>span{color:#717784}.unit-response-summary{display:grid;gap:12px;grid-template-columns:repeat(2,minmax(180px,1fr));margin-top:14px}.unit-response-summary article{background:#f7f8fa;border-left:4px solid #c0001d;border-radius:8px;display:grid;gap:3px;padding:14px}.unit-response-summary article:last-child{border-color:#ba7517}.unit-response-summary article.complete{border-color:#2f7b42}.unit-response-summary strong{font-size:24px}.unit-response-summary span{color:#68707d;font-size:12px}.pending-units{border-top:1px solid #eceef2;margin-top:14px;padding-top:14px}.pending-units>strong{font-size:12px;text-transform:uppercase}.pending-units ul{display:flex;flex-wrap:wrap;gap:8px;list-style:none;margin:10px 0 0;padding:0}.pending-units li{background:#fff4df;border:1px solid #f1d7a9;border-radius:999px;color:#6e4a12;font-size:12px;padding:6px 10px}.pending-units li span{color:#806a48}.all-answered{color:#2f7b42;font-weight:700;margin:14px 0 0}.filters-card,.table-card{background:#fff;border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;padding:16px}.table-heading{align-items:center;display:flex;justify-content:space-between}.table-heading div{display:grid;gap:3px}.table-heading small,.table-heading span{color:#717784}.not-met{background:#e24b4a}.basic{background:#ba7517}.sustainable{background:#3b6d11}.high{background:#0c447c}.filters-card{display:grid;gap:12px;grid-template-columns:repeat(4,1fr) 2fr}.report-actions{align-items:center;border-top:1px solid #eceef2;display:flex;grid-column:1/-1;justify-content:space-between;padding-top:14px}.report-actions>div{display:grid;gap:3px}.report-actions small{color:#717784}.publication-badge{align-items:center;display:inline-flex;font-size:12px;font-weight:700;gap:7px}.publication-badge:before{background:#ba7517;border-radius:50%;content:"";height:8px;width:8px}.publication-badge.ready:before{background:#245b92}.publication-badge.published:before{background:#246a3a}.publication-buttons{display:flex!important;flex-wrap:wrap;gap:8px!important;justify-content:flex-end}.report-button{background:#a5001a;border:0;border-radius:7px;color:#fff;cursor:pointer;font-weight:700;min-height:44px;padding:10px 18px}.report-button:disabled{background:#c9cbd1;color:#fff;cursor:not-allowed}.danger{background:#fff;border:1px solid #a5001a;color:#a5001a}.report-error{color:#a5001a;grid-column:1/-1;margin:0}.table-card{padding:0}.table-heading{padding:16px}.table-wrap{overflow:auto}table{border-collapse:collapse;min-width:1500px;width:100%}.divergence-table{min-width:900px}th{background:#f7f8fa;color:#646b76;font-size:11px;text-align:left;text-transform:uppercase}th,td{border-top:1px solid #eceef2;padding:12px}td strong,td small{display:block}td small{color:#737986;margin-bottom:3px}.layer-badge{border-radius:999px;color:#fff;display:inline-flex;font-size:11px;font-weight:700;padding:5px 9px}.never-edited{color:#8a9099;font-size:12px}.attachment-button{background:none;border:0;color:#a5001a;cursor:pointer;display:block;font-size:12px;padding:2px 0;text-align:left}.edit-button,.primary,.secondary,.state-message button{border:0;border-radius:6px;cursor:pointer;font-weight:700;padding:9px 12px}.edit-button,.primary{background:#a5001a;color:#fff}.secondary{background:#eceef2;color:#333}.empty,.state-message{padding:28px;text-align:center}.state-message.error,.editor-error{color:#a5001a}.sgu-editor-overlay{align-items:center;background:#12151bcc;display:flex;inset:0;justify-content:center;padding:24px;position:fixed;z-index:3000}.editor-modal{background:#fff;border-radius:12px;max-height:92vh;max-width:1050px;opacity:1;overflow:auto;padding:22px;width:100%}.editor-modal>header{align-items:flex-start;display:flex;justify-content:space-between}.editor-modal h2{font-size:20px;margin:4px 0 0}.editor-modal>header button{background:none;border:0;font-size:28px}.audit-line{background:#f6f7f9;border-radius:7px;display:grid;font-size:12px;gap:4px;margin:14px 0;padding:11px}.reference-grid{display:grid;gap:10px;grid-template-columns:repeat(3,1fr)}.reference-grid article{border:1px solid #e3e5e9;border-radius:8px;padding:12px}.reference-grid p{font-size:12px;line-height:1.5;margin:7px 0 0}.editor-fields{display:grid;gap:12px;margin-top:14px}.editor-fields b{color:#a5001a}.editor-fields a{font-size:12px}.editor-modal footer{display:flex;gap:8px;justify-content:flex-end;margin-top:18px}@media(max-width:900px){.unit-response-summary{grid-template-columns:1fr}.filters-card,.reference-grid{grid-template-columns:1fr}.report-actions{align-items:stretch;display:grid;gap:10px}.publication-buttons{justify-content:flex-start}.responses-header{align-items:stretch;display:grid;gap:16px}.responses-page{padding:12px}}
.responses-header{align-items:center}.responses-header-actions label{align-items:center;display:flex;gap:8px;margin:0}.responses-header-actions select{box-sizing:border-box;height:44px;min-height:44px}.filters-card{grid-template-columns:repeat(4,1fr) 2fr}@media(max-width:900px){.responses-header-actions{align-items:center;display:flex;flex-wrap:wrap}.header-report-button{align-self:auto}.filters-card{grid-template-columns:1fr}}@media(max-width:520px){.responses-header-actions{align-items:stretch;display:grid;grid-template-columns:1fr}.responses-header-actions label{display:grid;grid-template-columns:auto minmax(0,1fr)}.header-report-button{width:100%}}
.not-applicable{background:#737982}
</style>
